import { useRef, useState } from "react";
import {
  Button,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
} from "@mui/material";

const CLOSE_CONFIRMATION_MESSAGE = "Are you sure you want to close this window?";

type DialogCloseHandler = NonNullable<DialogProps["onClose"]>;

type PendingClose = {
  event: Parameters<DialogCloseHandler>[0];
  reason: Parameters<DialogCloseHandler>[1];
};

const ConfirmableDialog = ({ onClose, children, ...props }: DialogProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingCloseRef = useRef<PendingClose | null>(null);

  const handleClose: DialogCloseHandler = (event, reason) => {
    if (reason !== "backdropClick") {
      onClose?.(event, reason);
      return;
    }

    if (confirmOpen) {
      return;
    }

    pendingCloseRef.current = { event, reason };
    setConfirmOpen(true);
  };

  const handleCancelClose = () => {
    pendingCloseRef.current = null;
    setConfirmOpen(false);
  };

  const handleConfirmClose = () => {
    const pendingClose = pendingCloseRef.current;
    pendingCloseRef.current = null;
    setConfirmOpen(false);

    if (pendingClose) {
      onClose?.(pendingClose.event, pendingClose.reason);
    }
  };

  return (
    <>
      <MuiDialog {...props} onClose={handleClose}>
        {children}
      </MuiDialog>
      <MuiDialog open={confirmOpen} onClose={undefined} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm close</DialogTitle>
        <DialogContent>
          <DialogContentText>{CLOSE_CONFIRMATION_MESSAGE}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>No, Cancel</Button>
          <Button
            onClick={handleConfirmClose}
            variant="contained"
            color="primary"
            autoFocus
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
};

export default ConfirmableDialog;
